import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService, LazyLoadEvent, MessageService } from 'primeng';
import { Subject } from 'rxjs';
import { Message } from 'src/app/classes/Message';
import { IOrganisationAdditionalChargeTypeFilter } from 'src/app/interfaces/organisation-additional-charge-filter';
import { IOrganisationAdditionalChargeType } from 'src/app/interfaces/organisation-additional-charge-type';
import { IPageResult } from 'src/app/interfaces/page-result';
import { AdditionalChargeService, CustomerService, OrganisationsService } from 'src/app/services';
import { OrganisationAdditionalChargeTypeService } from 'src/app/services/organisation-additional-charge-type.services';
import { flatten } from '../../utils/flatten';
@Component({
  selector: 'app-organisation-additional-charge-type',
  templateUrl: './organisation-additional-charge-type.component.html',
  styleUrls: ['./organisation-additional-charge-type.component.css'],
})
export class OrganisationAdditionalChargeTypeComponent implements OnInit {
  private _manageMode: number;
  @Input('manageMode')
  set manageMode(value: number) {
    this._manageMode = value;
  }
  get manageMode(): number {
    return this._manageMode;
  }

  public isSettingsFormEnabled: boolean = false;
  public settingsForm: FormGroup;
  public isSaving: boolean = false;
  public additionalChargeTypes: any[];
  public organisationTypes: any[];
  public selectedOrganisationId: number;
  public filterVal: IOrganisationAdditionalChargeTypeFilter;
  public filter: Subject<IOrganisationAdditionalChargeTypeFilter>;
  public page: IPageResult<IOrganisationAdditionalChargeType> = {
    items: [],
    total: 0,
  };
  public cols = [];
  constructor(
    private orgAdditionalChargeTypeService: OrganisationAdditionalChargeTypeService,
    private additionalChargeService: AdditionalChargeService,
    private customerService: CustomerService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
  ) {}

  ngOnInit() {
    this.loadDropDownItems();
    this.filterVal = {
      first: 0,
      rows: 10,
      sortField: null,
      sortOrder: 1,
      filters: {
        organisationId: { value: null },
      },
    };
    this.filter = new Subject<IOrganisationAdditionalChargeTypeFilter>();
    this.filter.debounceTime(500).subscribe((f) => {
      this.loadOrgAdditionalChargeTypes(f);
      this.filterVal = f;
    });
  }

  loadOrgAdditionalChargeTypes(filter: IOrganisationAdditionalChargeTypeFilter) {
    this.orgAdditionalChargeTypeService.getAll(flatten(filter)).then((res) => {
      this.page = res;
    });
  }

  loadDropDownItems(isNew: boolean = false) {
    // get additional charge Types
    this.additionalChargeService.getAdditionalChargeTypes().then((result) => {
      this.additionalChargeTypes = [];
      this.additionalChargeTypes.push({ value: null, label: 'Select Charge Type...' });
      result.forEach((x) => {
        this.additionalChargeTypes.push({ label: x.chargeType, value: x.id });
      });
    });
  }

  onOrganisationChange(orgId: number) {
    if (orgId) {
      this.settingsForm.controls['organisationId'].setValue(orgId);
    }
  }

  resetloadOrgAdditionalChargeTypesForm() {
    this.settingsForm = new FormGroup(
      {
        id: new FormControl(0),
        organisationId: new FormControl(null, Validators.compose([Validators.required])),
        additionalChargeTypeId: new FormControl(null, Validators.compose([Validators.required])),
        chargeCustomer: new FormControl(false, Validators.compose([Validators.required])),
        chargeSupplier: new FormControl(false, Validators.compose([Validators.required])),
        chargeEngineiusCustomer: new FormControl(false, Validators.compose([Validators.required])),
        chargeEngineiusSupplier: new FormControl(false, Validators.compose([Validators.required])),
      },
      { updateOn: 'submit' },
    );
  }
  showSettingsFormDialog = () => {
    this.resetloadOrgAdditionalChargeTypesForm();
    this.isSettingsFormEnabled = true;
  };
  saveOrgAdditionalChargeType(formValue) {
    if (formValue && this.settingsForm.valid) {
      this.isSaving = true;
      if (!formValue.id) {
        this.orgAdditionalChargeTypeService
          .create(formValue)
          .then((res) => {
            this.reloadTransactions();
            this.isSettingsFormEnabled = false;
            this.isSaving = false;
          })
          .catch((err) => {
            console.error('Error Creating New Organisation Additional Charge Setting', err);
            this.messageService.add(Message.Error(err.error, 'Error'));
            this.isSaving = false;
          });
      } else {
        this.orgAdditionalChargeTypeService
          .update(formValue.id, formValue)
          .then((res) => {
            this.reloadTransactions();
            this.isSettingsFormEnabled = false;
            this.isSaving = false;
          })
          .catch((err) => {
            console.error('Error updating New Organisation Additional Charge Setting', err);
            this.messageService.add(Message.Error(err.error, 'Error'));
            this.isSaving = false;
          });
      }
    }
    this.selectedOrganisationId = null;
  }
  modifyOrgAdditionalChargeType(setting: IOrganisationAdditionalChargeType) {
    this.showSettingsFormDialog();
    if (setting) {
      this.selectedOrganisationId = setting.organisationId;
      this.settingsForm = new FormGroup(
        {
          id: new FormControl(setting.id),
          organisationId: new FormControl(
            setting.organisationId,
            Validators.compose([Validators.required]),
          ),
          additionalChargeTypeId: new FormControl(
            setting.additionalChargeTypeId,
            Validators.compose([Validators.required]),
          ),
          chargeCustomer: new FormControl(
            setting.chargeCustomer,
            Validators.compose([Validators.required]),
          ),
          chargeSupplier: new FormControl(
            setting.chargeSupplier,
            Validators.compose([Validators.required]),
          ),
          chargeEngineiusCustomer: new FormControl(
            setting.chargeEngineiusCustomer,
            Validators.compose([Validators.required]),
          ),
          chargeEngineiusSupplier: new FormControl(
            setting.chargeEngineiusSupplier,
            Validators.compose([Validators.required]),
          ),
        },
        { updateOn: 'submit' },
      );
    }
  }
  deleteOrgAdditionalChargeType(setting) {
    // confirm you wish to delete
    if (setting.id) {
      this.confirmationService.confirm({
        message: `Confirm you want to delete this setting '${setting.organisationName} - ${setting.additionalChargeTypeName}'?`,
        accept: () => {
          this.orgAdditionalChargeTypeService
            .delete(setting.id)
            .then((res) => {
              this.reloadTransactions();
            })
            .catch((err) => {
              console.log(err);
            });
        },
        reject: () => console.log('User selected to not delete setting'),
      });
    }
  }

  handleChargeableEntityChange(evt, chargedEntity: string) {
    if (evt) {
      // this has now been checked, otherwise its just unchecking
      if (chargedEntity == 'isChargeEngineiusSupplier') {
        // uncheck chargeSupplier
        this.settingsForm.controls['chargeSupplier'].setValue(false);
        this.settingsForm.controls['chargeEngineiusCustomer'].setValue(false);
      } else if (chargedEntity == 'isChargeSupplier') {
        // uncheck chargeEngineiusSupplier
        this.settingsForm.controls['chargeEngineiusSupplier'].setValue(false);
      } else if (chargedEntity == 'isChargeCustomer') {
        // uncheck chargeEngineiusCustomer
        this.settingsForm.controls['chargeEngineiusCustomer'].setValue(false);
      } else if (chargedEntity == 'isChargeEngineiusCustomer') {
        // uncheck chargeCustomer
        this.settingsForm.controls['chargeCustomer'].setValue(false);
        this.settingsForm.controls['chargeEngineiusSupplier'].setValue(false);
      }
    }
  }

  onFilterOrganisationChange(organisationId: number) {
    this.filterVal.filters.organisationId.value = organisationId;
    if (organisationId > 0) this.filterVal.filters.organisationId.matchMode = 'equals';
    else this.filterVal.filters.organisationId.matchMode = null;
  }
  search = () => this.reloadTransactions();

  reloadTransactions = () => {
    this.filterVal.first = 0;
    this.filter.next(this.filterVal);
  };

  onGridLazyLoad(event: LazyLoadEvent) {
    this.filterVal.first = event.first;
    this.filterVal.rows = event.rows;
    this.filterVal.sortField = event.sortField;
    this.filterVal.sortOrder = event.sortOrder;
    this.filter.next(this.filterVal);
  }
}
